/**
 * Google Analytics Tracking Utility
 * 
 * Simple wrapper for Google Analytics 4 (GA4) tracking
 */

declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
    dataLayer?: any[];
  }
}

class Analytics {
  private initialized = false;
  private measurementId: string = '';

  /**
   * Initialize Google Analytics
   */
  init(measurementId: string) {
    if (this.initialized) {
      return;
    }

    this.measurementId = measurementId;

    // Load GA4 script
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
    document.head.appendChild(script);

    // Initialize dataLayer
    window.dataLayer = window.dataLayer || [];
    window.gtag = function () {
      window.dataLayer?.push(arguments);
    };

    window.gtag('js', new Date());
    window.gtag('config', measurementId, {
      send_page_view: true,
    });

    this.initialized = true;
  }

  /**
   * Track page view
   */
  pageView(path: string, title?: string) {
    if (!this.initialized) return;

    window.gtag?.('event', 'page_view', {
      page_path: path,
      page_title: title || document.title,
    });
  }

  /**
   * Track custom event
   */
  event(eventName: string, parameters?: Record<string, any>) {
    if (!this.initialized) return;

    window.gtag?.('event', eventName, parameters);
  }

  /**
   * Track product view
   */
  viewProduct(productId: string, productName: string, price: number) {
    this.event('view_item', {
      items: [{
        item_id: productId,
        item_name: productName,
        price: price,
      }],
    });
  }

  /**
   * Track add to cart
   */
  addToCart(productId: string, productName: string, price: number, quantity: number = 1) {
    this.event('add_to_cart', {
      currency: 'MYR',
      value: price * quantity,
      items: [{
        item_id: productId,
        item_name: productName,
        price: price,
        quantity: quantity,
      }],
    });
  }

  /**
   * Track purchase
   */
  purchase(
    transactionId: string,
    value: number,
    items: Array<{
      item_id: string;
      item_name: string;
      price: number;
      quantity: number;
    }>,
  ) {
    this.event('purchase', {
      transaction_id: transactionId,
      currency: 'MYR',
      value: value,
      items: items,
    });
  }

  /**
   * Track event booking
   */
  bookEvent(eventId: string, eventName: string, pax: number, amount: number) {
    this.event('book_event', {
      event_id: eventId,
      event_name: eventName,
      pax: pax,
      amount: amount,
      currency: 'MYR',
    });
  }

  /**
   * Track user signup
   */
  signUp(method: string = 'email') {
    this.event('sign_up', {
      method: method,
    });
  }

  /**
   * Track user login
   */
  login(method: string = 'email') {
    this.event('login', {
      method: method,
    });
  }

  /**
   * Track search
   */
  search(searchTerm: string) {
    this.event('search', {
      search_term: searchTerm,
    });
  }

  /**
   * Set user properties
   */
  setUser(userId: string, properties?: Record<string, any>) {
    if (!this.initialized) return;

    window.gtag?.('set', 'user_properties', {
      user_id: userId,
      ...properties,
    });
  }
}

// Export singleton instance
export const analytics = new Analytics();

// Auto-initialize if GA_MEASUREMENT_ID is in environment
if (import.meta.env.VITE_GA_MEASUREMENT_ID) {
  analytics.init(import.meta.env.VITE_GA_MEASUREMENT_ID);
}
